import { PlusIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import LoyaltyRewardModal from "../components/LoyaltyRewardModal";
import Toast from "../components/Toast";
import { apiErrorMessage } from "../services/api";
import { fetchBranches } from "../services/branchService";
import { fetchRewards, updateReward } from "../services/loyaltyService";
import { fetchCatalogue } from "../services/serviceService";
import { labelFor } from "../services/enumLabels";

/**
 * Loyalty → Rewards (`loyalty.manage`). What points and stamps can be turned
 * into. Deactivating keeps a reward (and every claim of it) but takes it off
 * offer; a reward already claimed by somebody keeps working until used.
 */
export default function LoyaltyRewards() {
  const { t } = useTranslation();
  const [rewards, setRewards] = useState(null);
  const [lookups, setLookups] = useState({ branches: [], services: [] });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(undefined); // undefined closed, null new, object edit
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setError("");
    fetchRewards()
      .then(setRewards)
      .catch((err) => setError(apiErrorMessage(err, t("loyalty.loadFailed"))));
  }, [t]);

  useEffect(() => {
    load();
    // Products are searched in the form itself (RewardProductPicker).
    Promise.all([fetchBranches(1, 100), fetchCatalogue({ perPage: 100 })])
      .then(([b, s]) => setLookups({ branches: b.branches ?? [], services: s.services ?? [] }))
      .catch(() => {});
  }, [load]);

  const toggleActive = async (reward) => {
    setBusyId(reward.id);
    try {
      await updateReward(reward.id, {
        name: reward.translations?.name,
        type: reward.type,
        points_required: reward.points_required,
        stamps_required: reward.stamps_required,
        product_id: reward.product_id ?? null,
        // Sent back as they are: a free service must keep at least one.
        services: (reward.services ?? []).map((s) => s.id),
        is_active: !reward.is_active,
      });
      setToast({
        type: "success",
        message: t(reward.is_active ? "loyalty.rewards.deactivated" : "loyalty.rewards.activated"),
      });
      load();
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("loyalty.rewards.saveFailed")) });
    } finally {
      setBusyId(null);
    }
  };

  const cost = (reward) =>
    [
      reward.points_required > 0 && t("loyalty.pointsCount", { count: reward.points_required }),
      reward.stamps_required > 0 && t("loyalty.stampsCount", { count: reward.stamps_required }),
    ]
      .filter(Boolean)
      .join(" + ");

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("loyalty.rewards.title")}</h1>
          <p className="text-sm text-ink-2">{t("loyalty.rewards.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary inline-flex items-center gap-1"
          onClick={() => setEditing(null)}
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          {t("loyalty.rewards.new")}
        </button>
      </div>

      {error && (
        <div
          className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {rewards === null ? (
          <p className="py-12 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : rewards.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-2">{t("loyalty.rewards.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3 text-start">{t("loyalty.rewards.name")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.rewards.type")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.rewards.cost")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.rewards.where")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.rewards.claimed")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.rewards.status")}</th>
                  <th className="px-4 py-3 text-end">{t("loyalty.rewards.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rewards.map((reward) => (
                  <tr key={reward.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{reward.name}</p>
                      {reward.product && <p className="text-xs text-ink-2">{reward.product.name}</p>}
                      {reward.type === "free_service" && reward.services?.length > 0 && (
                        <p className="text-xs text-ink-2">{reward.services.map((s) => s.name).join("، ")}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {labelFor("reward_type", reward.type)}
                      {reward.value && (
                        <span dir="ltr" className="ms-1">
                          ({reward.type === "discount_percent" ? `${Number(reward.value)}%` : reward.value})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{cost(reward)}</td>
                    <td className="px-4 py-3 text-xs text-ink-2">
                      {reward.all_branches
                        ? t("loyalty.rewards.allBranches")
                        : (reward.branches ?? []).map((b) => b.name).join(", ")}
                    </td>
                    <td className="px-4 py-3" dir="ltr">
                      {reward.redemption_count}
                      {reward.total_redemption_limit ? ` / ${reward.total_redemption_limit}` : ""}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          reward.is_active ? "bg-accent-wash text-accent-text" : "bg-raised text-ink-2"
                        }`}
                      >
                        {reward.is_active
                          ? t("loyalty.rewards.activeBadge")
                          : t("loyalty.rewards.inactiveBadge")}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-end">
                      <button type="button" className="btn-secondary me-2" onClick={() => setEditing(reward)}>
                        {t("common.edit")}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={busyId === reward.id}
                        onClick={() => toggleActive(reward)}
                      >
                        {reward.is_active ? t("loyalty.rewards.deactivate") : t("loyalty.rewards.activate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LoyaltyRewardModal
        open={editing !== undefined}
        reward={editing ?? null}
        branches={lookups.branches}
        services={lookups.services}
        onClose={() => setEditing(undefined)}
        onSaved={(saved, wasEdit) => {
          setToast({
            type: "success",
            message: t(wasEdit ? "loyalty.rewards.updated" : "loyalty.rewards.created", { name: saved.name }),
          });
          load();
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
